export type PlanDetail = {
  name: string
  price: string
  annual_monthly_price: string
  plan_id: string
  no_trial_plan_id: string
  yearly_plan_id: string
  description: string
  features: string[]
  annual_price: string
  summary: string
  amount_saved: string
}

export const currencySymbols: { [key: string]: string } = {
  USD: '$',
  EUR: '€',
  GBP: '£',
  JPY: '¥',
  // Add more currencies as needed
}

export const getCurrencySymbol = (currencyCode: string): string => {
  return currencySymbols[currencyCode] || currencyCode // Fallback to code if symbol not found
}

export const FREE_PLAN_ID = 'creative-os-free-plan'

export type COUPON_DETAILS = {
  id: string
  name: string
  status: string
  discountType: string
  durationType: string
  discountAmount?: number | undefined
  discountPercentage?: number | undefined
  currencyCode?: string | undefined
  period?: number | undefined
  periodUnit?: string | undefined
}

export const PRO_DOWNGRADE_COUPON = '40$OFF'
export const IGNITE_DOWNGRADE_COUPON = '10$OFF'

export const TEAMS_EXTRA_SEAT_PRICE = 19

export type SUBSCRIPTION_STATUSES = 'onFreeTrial' | 'onFreePlan' | 'hasPlan' | 'hasNoPlan' | undefined

export const FREE_PLAN = {
  name: 'Free Plan',
  price: 'Free',
  annual_price: '$299',
  annual_monthly_price: '$25',
  amount_saved: '$289',
  plan_id: 'creative-os-free-plan',
  yearly_plan_id: 'creative-os-free-plan',
  no_trial_plan_id: 'creative-os-free-plan',
  description: 'The perfect plan to find inspiration. Fast.',
  summary: 'The perfect plan to find inspiration. Fast.',
  features: ['Search Thousands of Ads', 'Full Inspiration Library', '1 Inspiration Collection'],
}

export const STANDARD_PLAN = {
  name: 'Ignite Plan',
  price: '$49',
  annual_price: '$299',
  annual_monthly_price: '$25',
  amount_saved: '$289',
  amount_saved_per_month: 10,
  plan_id: 'pln_creative-os-standard-hoq20qmr',
  yearly_plan_id: 'pln_creative-os-standard-hoq20qmr-usd-yearly',
  no_trial_plan_id: 'creativeos-standard-no-trial',
  description: "Fuel your brand's creative journey with enhanced features and powerful tools.",
  summary: 'The perfect plan to start testing ads faster. 3-Day Trial, Cancel Anytime.',
  features: [
    'Everything in Spark',
    'Unlimited Ad Templates',
    'Ad, Email & Lander Requests',
    'Industry Expert Packs',
    '10 Collections',
  ],
  // nonFeatures: ['Request New Templates', 'Email and Landing Pages Templates'],
}

export type DOWNGRADE_PLANS =
  | 'pln_creative-os-standard-hoq20qmr'
  | 'creativeos-standard-no-trial'
  | 'creative-os-free-plan'

export const PRO_PLAN = {
  name: 'Pro Plan',
  price: '$99',
  annual_price: '$499',
  amount_saved: '$689',
  amount_saved_per_month: 40,
  annual_monthly_price: '$41',
  plan_id: 'pln_creative-os-pro-hdpx0qjo',
  yearly_plan_id: 'pln_creative-os-pro-hdpx0qjo-USD-Yearly',
  no_trial_plan_id: 'creativeos-pro-no-trial',
  description: 'Take your brands creativity to the next level with premium, advanced tools to maximize potential.',
  summary: "Supercharge your brand's creative output. 3-Day Trial, Cancel Anytime.",
  features: [
    'Everything in Ignite',
    'Unlimited Template Requests',
    'Unlimited Template Usage',
    'Unlimited Collections',
    'Unlimited Access To All Features',
    'Early Access to All Future Features',
  ],
}

export const TEAMS_PLAN = {
  name: 'Teams Plan',
  price: '$149',
  annual_price: '$149',
  amount_saved: '$689',
  amount_saved_per_month: 40,
  annual_monthly_price: '$41',
  plan_id: 'team-pln-usd-monthly',
  yearly_plan_id: 'team-pln-usd-monthly',
  no_trial_plan_id: 'team-pln-usd-monthly',
  description: 'Take your brands creativity to the next level with premium, advanced tools to maximize potential.',
  summary: "Supercharge your brand's creative output. 3-Day Trial, Cancel Anytime.",
  features: [
    'Everything in Pro, PLUS',
    '3 teammates (included)',
    '500GB of storage',
    'Intelligent Asset Identification (coming soon)',
  ],
}

export const VISION_PASS = {
  name: 'Vision Pass Plan',
  price: '$599',
  annual_price: '$599',
  amount_saved: '$0',
  amount_saved_per_month: 40,
  annual_monthly_price: '$41',
  plan_id: 'lifetime-pln-usd',
  yearly_plan_id: 'lifetime-pln-usd',
  no_trial_plan_id: 'lifetime-pln-usd',
  description: 'Take your brands creativity to the next level with premium, advanced tools to maximize potential.',
  summary: "Supercharge your brand's creative output. 3-Day Trial, Cancel Anytime.",
  features: [
    'Everything in Ignite',
    'Request New Templates',
    'Email Template Library',
    'Landing Page Templates',
    'Industry Expert Packs',
  ],
}

export const DROP_SHIPPER = {
  name: 'Vision Pass Plan',
  price: '$349',
  annual_price: '$349',
  amount_saved: '$689',
  amount_saved_per_month: 40,
  annual_monthly_price: '$41',
  plan_id: 'dropshipper-usd',
  yearly_plan_id: 'dropshipper-usd',
  no_trial_plan_id: 'dropshipper-usd',
  description: 'Take your brands creativity to the next level with premium, advanced tools to maximize potential.',
  summary: "Supercharge your brand's creative output. 3-Day Trial, Cancel Anytime.",
  features: [
    'Everything in Ignite',
    'Request New Templates',
    'Email Template Library',
    'Landing Page Templates',
    'Industry Expert Packs',
  ],
}

export const SPARK_PLAN = {
  name: 'Spark Plan',
  price: '$19',
  annual_price: '$299',
  amount_saved: '$689',
  amount_saved_per_month: 40,
  annual_monthly_price: '$41',
  plan_id: 'creativeos-spark-plan-usd-monthly',
  yearly_plan_id: 'pln_creative-os-pro-hdpx0qjo-USD-Yearly',
  no_trial_plan_id: 'spark-plan-no-trial-usd-Monthly',
  description: "Jumpstart your brand's creative process with essential tools and features.",
  summary: "Jumpstart your brand's creative process with essential tools and features.",
  features: ['5,500+ Static Ad Templates', 'Email and Lander Templates', '3 Collections', 'Creative Toolkit'],
  nonFeatures: ['Request New Templates', 'Industry Expert Packs'],
}

export const DOWNGRADE_COUPON = 'FARIS'

export const plans: PlanDetail[] = [FREE_PLAN, SPARK_PLAN, STANDARD_PLAN, PRO_PLAN]

export const countries = [
  { name: 'Afghanistan', code: 'AF' },
  { name: 'Åland Islands', code: 'AX' },
  { name: 'Albania', code: 'AL' },
  { name: 'Algeria', code: 'DZ' },
  { name: 'American Samoa', code: 'AS' },
  { name: 'AndorrA', code: 'AD' },
  { name: 'Angola', code: 'AO' },
  { name: 'Anguilla', code: 'AI' },
  { name: 'Antarctica', code: 'AQ' },
  { name: 'Antigua and Barbuda', code: 'AG' },
  { name: 'Argentina', code: 'AR' },
  { name: 'Armenia', code: 'AM' },
  { name: 'Aruba', code: 'AW' },
  { name: 'Australia', code: 'AU' },
  { name: 'Austria', code: 'AT' },
  { name: 'Azerbaijan', code: 'AZ' },
  { name: 'Bahamas', code: 'BS' },
  { name: 'Bahrain', code: 'BH' },
  { name: 'Bangladesh', code: 'BD' },
  { name: 'Barbados', code: 'BB' },
  { name: 'Belarus', code: 'BY' },
  { name: 'Belgium', code: 'BE' },
  { name: 'Belize', code: 'BZ' },
  { name: 'Benin', code: 'BJ' },
  { name: 'Bermuda', code: 'BM' },
  { name: 'Bhutan', code: 'BT' },
  { name: 'Bolivia', code: 'BO' },
  { name: 'Bosnia and Herzegovina', code: 'BA' },
  { name: 'Botswana', code: 'BW' },
  { name: 'Bouvet Island', code: 'BV' },
  { name: 'Brazil', code: 'BR' },
  { name: 'British Indian Ocean Territory', code: 'IO' },
  { name: 'Brunei Darussalam', code: 'BN' },
  { name: 'Bulgaria', code: 'BG' },
  { name: 'Burkina Faso', code: 'BF' },
  { name: 'Burundi', code: 'BI' },
  { name: 'Cambodia', code: 'KH' },
  { name: 'Cameroon', code: 'CM' },
  { name: 'Canada', code: 'CA' },
  { name: 'Cape Verde', code: 'CV' },
  { name: 'Cayman Islands', code: 'KY' },
  { name: 'Central African Republic', code: 'CF' },
  { name: 'Chad', code: 'TD' },
  { name: 'Chile', code: 'CL' },
  { name: 'China', code: 'CN' },
  { name: 'Christmas Island', code: 'CX' },
  { name: 'Cocos (Keeling) Islands', code: 'CC' },
  { name: 'Colombia', code: 'CO' },
  { name: 'Comoros', code: 'KM' },
  { name: 'Congo', code: 'CG' },
  { name: 'Congo, The Democratic Republic of the', code: 'CD' },
  { name: 'Cook Islands', code: 'CK' },
  { name: 'Costa Rica', code: 'CR' },
  { name: "Cote D'Ivoire", code: 'CI' },
  { name: 'Croatia', code: 'HR' },
  { name: 'Cuba', code: 'CU' },
  { name: 'Cyprus', code: 'CY' },
  { name: 'Czech Republic', code: 'CZ' },
  { name: 'Denmark', code: 'DK' },
  { name: 'Djibouti', code: 'DJ' },
  { name: 'Dominica', code: 'DM' },
  { name: 'Dominican Republic', code: 'DO' },
  { name: 'Ecuador', code: 'EC' },
  { name: 'Egypt', code: 'EG' },
  { name: 'El Salvador', code: 'SV' },
  { name: 'Equatorial Guinea', code: 'GQ' },
  { name: 'Eritrea', code: 'ER' },
  { name: 'Estonia', code: 'EE' },
  { name: 'Ethiopia', code: 'ET' },
  { name: 'Falkland Islands (Malvinas)', code: 'FK' },
  { name: 'Faroe Islands', code: 'FO' },
  { name: 'Fiji', code: 'FJ' },
  { name: 'Finland', code: 'FI' },
  { name: 'France', code: 'FR' },
  { name: 'French Guiana', code: 'GF' },
  { name: 'French Polynesia', code: 'PF' },
  { name: 'French Southern Territories', code: 'TF' },
  { name: 'Gabon', code: 'GA' },
  { name: 'Gambia', code: 'GM' },
  { name: 'Georgia', code: 'GE' },
  { name: 'Germany', code: 'DE' },
  { name: 'Ghana', code: 'GH' },
  { name: 'Gibraltar', code: 'GI' },
  { name: 'Greece', code: 'GR' },
  { name: 'Greenland', code: 'GL' },
  { name: 'Grenada', code: 'GD' },
  { name: 'Guadeloupe', code: 'GP' },
  { name: 'Guam', code: 'GU' },
  { name: 'Guatemala', code: 'GT' },
  { name: 'Guernsey', code: 'GG' },
  { name: 'Guinea', code: 'GN' },
  { name: 'Guinea-Bissau', code: 'GW' },
  { name: 'Guyana', code: 'GY' },
  { name: 'Haiti', code: 'HT' },
  { name: 'Heard Island and Mcdonald Islands', code: 'HM' },
  { name: 'Holy See (Vatican City State)', code: 'VA' },
  { name: 'Honduras', code: 'HN' },
  { name: 'Hong Kong', code: 'HK' },
  { name: 'Hungary', code: 'HU' },
  { name: 'Iceland', code: 'IS' },
  { name: 'India', code: 'IN' },
  { name: 'Indonesia', code: 'ID' },
  { name: 'Iran, Islamic Republic Of', code: 'IR' },
  { name: 'Iraq', code: 'IQ' },
  { name: 'Ireland', code: 'IE' },
  { name: 'Isle of Man', code: 'IM' },
  { name: 'Israel', code: 'IL' },
  { name: 'Italy', code: 'IT' },
  { name: 'Jamaica', code: 'JM' },
  { name: 'Japan', code: 'JP' },
  { name: 'Jersey', code: 'JE' },
  { name: 'Jordan', code: 'JO' },
  { name: 'Kazakhstan', code: 'KZ' },
  { name: 'Kenya', code: 'KE' },
  { name: 'Kiribati', code: 'KI' },
  { name: "Korea, Democratic People'S Republic of", code: 'KP' },
  { name: 'Korea, Republic of', code: 'KR' },
  { name: 'Kuwait', code: 'KW' },
  { name: 'Kyrgyzstan', code: 'KG' },
  { name: "Lao People'S Democratic Republic", code: 'LA' },
  { name: 'Latvia', code: 'LV' },
  { name: 'Lebanon', code: 'LB' },
  { name: 'Lesotho', code: 'LS' },
  { name: 'Liberia', code: 'LR' },
  { name: 'Libyan Arab Jamahiriya', code: 'LY' },
  { name: 'Liechtenstein', code: 'LI' },
  { name: 'Lithuania', code: 'LT' },
  { name: 'Luxembourg', code: 'LU' },
  { name: 'Macao', code: 'MO' },
  { name: 'Macedonia, The Former Yugoslav Republic of', code: 'MK' },
  { name: 'Madagascar', code: 'MG' },
  { name: 'Malawi', code: 'MW' },
  { name: 'Malaysia', code: 'MY' },
  { name: 'Maldives', code: 'MV' },
  { name: 'Mali', code: 'ML' },
  { name: 'Malta', code: 'MT' },
  { name: 'Marshall Islands', code: 'MH' },
  { name: 'Martinique', code: 'MQ' },
  { name: 'Mauritania', code: 'MR' },
  { name: 'Mauritius', code: 'MU' },
  { name: 'Mayotte', code: 'YT' },
  { name: 'Mexico', code: 'MX' },
  { name: 'Micronesia, Federated States of', code: 'FM' },
  { name: 'Moldova, Republic of', code: 'MD' },
  { name: 'Monaco', code: 'MC' },
  { name: 'Mongolia', code: 'MN' },
  { name: 'Montserrat', code: 'MS' },
  { name: 'Morocco', code: 'MA' },
  { name: 'Mozambique', code: 'MZ' },
  { name: 'Myanmar', code: 'MM' },
  { name: 'Namibia', code: 'NA' },
  { name: 'Nauru', code: 'NR' },
  { name: 'Nepal', code: 'NP' },
  { name: 'Netherlands', code: 'NL' },
  { name: 'Netherlands Antilles', code: 'AN' },
  { name: 'New Caledonia', code: 'NC' },
  { name: 'New Zealand', code: 'NZ' },
  { name: 'Nicaragua', code: 'NI' },
  { name: 'Niger', code: 'NE' },
  { name: 'Nigeria', code: 'NG' },
  { name: 'Niue', code: 'NU' },
  { name: 'Norfolk Island', code: 'NF' },
  { name: 'Northern Mariana Islands', code: 'MP' },
  { name: 'Norway', code: 'NO' },
  { name: 'Oman', code: 'OM' },
  { name: 'Pakistan', code: 'PK' },
  { name: 'Palau', code: 'PW' },
  { name: 'Palestinian Territory, Occupied', code: 'PS' },
  { name: 'Panama', code: 'PA' },
  { name: 'Papua New Guinea', code: 'PG' },
  { name: 'Paraguay', code: 'PY' },
  { name: 'Peru', code: 'PE' },
  { name: 'Philippines', code: 'PH' },
  { name: 'Pitcairn', code: 'PN' },
  { name: 'Poland', code: 'PL' },
  { name: 'Portugal', code: 'PT' },
  { name: 'Puerto Rico', code: 'PR' },
  { name: 'Qatar', code: 'QA' },
  { name: 'Reunion', code: 'RE' },
  { name: 'Romania', code: 'RO' },
  { name: 'Russian Federation', code: 'RU' },
  { name: 'RWANDA', code: 'RW' },
  { name: 'Saint Helena', code: 'SH' },
  { name: 'Saint Kitts and Nevis', code: 'KN' },
  { name: 'Saint Lucia', code: 'LC' },
  { name: 'Saint Pierre and Miquelon', code: 'PM' },
  { name: 'Saint Vincent and the Grenadines', code: 'VC' },
  { name: 'Samoa', code: 'WS' },
  { name: 'San Marino', code: 'SM' },
  { name: 'Sao Tome and Principe', code: 'ST' },
  { name: 'Saudi Arabia', code: 'SA' },
  { name: 'Senegal', code: 'SN' },
  { name: 'Serbia and Montenegro', code: 'CS' },
  { name: 'Seychelles', code: 'SC' },
  { name: 'Sierra Leone', code: 'SL' },
  { name: 'Singapore', code: 'SG' },
  { name: 'Slovakia', code: 'SK' },
  { name: 'Slovenia', code: 'SI' },
  { name: 'Solomon Islands', code: 'SB' },
  { name: 'Somalia', code: 'SO' },
  { name: 'South Africa', code: 'ZA' },
  { name: 'South Georgia and the South Sandwich Islands', code: 'GS' },
  { name: 'Spain', code: 'ES' },
  { name: 'Sri Lanka', code: 'LK' },
  { name: 'Sudan', code: 'SD' },
  { name: 'Suriname', code: 'SR' },
  { name: 'Svalbard and Jan Mayen', code: 'SJ' },
  { name: 'Swaziland', code: 'SZ' },
  { name: 'Sweden', code: 'SE' },
  { name: 'Switzerland', code: 'CH' },
  { name: 'Syrian Arab Republic', code: 'SY' },
  { name: 'Taiwan, Province of China', code: 'TW' },
  { name: 'Tajikistan', code: 'TJ' },
  { name: 'Tanzania, United Republic of', code: 'TZ' },
  { name: 'Thailand', code: 'TH' },
  { name: 'Timor-Leste', code: 'TL' },
  { name: 'Togo', code: 'TG' },
  { name: 'Tokelau', code: 'TK' },
  { name: 'Tonga', code: 'TO' },
  { name: 'Trinidad and Tobago', code: 'TT' },
  { name: 'Tunisia', code: 'TN' },
  { name: 'Turkey', code: 'TR' },
  { name: 'Turkmenistan', code: 'TM' },
  { name: 'Turks and Caicos Islands', code: 'TC' },
  { name: 'Tuvalu', code: 'TV' },
  { name: 'Uganda', code: 'UG' },
  { name: 'Ukraine', code: 'UA' },
  { name: 'United Arab Emirates', code: 'AE' },
  { name: 'United Kingdom', code: 'GB' },
  { name: 'United States', code: 'US' },
  { name: 'United States Minor Outlying Islands', code: 'UM' },
  { name: 'Uruguay', code: 'UY' },
  { name: 'Uzbekistan', code: 'UZ' },
  { name: 'Vanuatu', code: 'VU' },
  { name: 'Venezuela', code: 'VE' },
  { name: 'Viet Nam', code: 'VN' },
  { name: 'Virgin Islands, British', code: 'VG' },
  { name: 'Virgin Islands, U.S.', code: 'VI' },
  { name: 'Wallis and Futuna', code: 'WF' },
  { name: 'Western Sahara', code: 'EH' },
  { name: 'Yemen', code: 'YE' },
  { name: 'Zambia', code: 'ZM' },
  { name: 'Zimbabwe', code: 'ZW' },
]
